import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useTheme } from '../contexts/ThemeContext';
import PushNotificationManager from './PushNotificationManager';
import NotificationPrompt from './NotificationPrompt';
import ShiftQuickControl from './ShiftQuickControl';
import { useGlobalTicketNotifications } from '../hooks/useGlobalTicketNotifications';
import {
  isNotificationSoundMuted,
  setNotificationSoundMuted,
} from '../utils/notificationSound';

import {
  Squares2X2Icon,
  TicketIcon,
  UsersIcon,
  ChartPieIcon,
  PresentationChartLineIcon,
  ClockIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  ArrowTrendingUpIcon,
  MegaphoneIcon,
  ArrowRightOnRectangleIcon,
  InboxStackIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  SunIcon,
  MoonIcon,
  ChatBubbleLeftRightIcon,
  TrophyIcon,
  DocumentChartBarIcon,
  BanknotesIcon,
  BookOpenIcon,
  BellIcon,
  BellSlashIcon,
} from '@heroicons/react/24/outline';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Global "new ticket" notifier: WS-subscribes once, plays a ding and
  // shows a click-through toast on every authenticated page.
  useGlobalTicketNotifications();
  // Sound mute toggle — persisted in localStorage by the util.
  const [soundMuted, setSoundMuted] = useState<boolean>(isNotificationSoundMuted());
  const toggleSound = () => {
    const next = !soundMuted;
    setNotificationSoundMuted(next);
    setSoundMuted(next);
  };
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useTheme();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Панель', href: '/dashboard', icon: Squares2X2Icon, shortName: 'Главная' },
    { name: 'Заявки', href: '/tickets', icon: TicketIcon, shortName: 'Заявки' },
    { name: 'Рабочее окно', href: '/work', icon: InboxStackIcon, shortName: 'Окно' },
    { name: 'Клиенты', href: '/clients', icon: ChatBubbleLeftRightIcon, shortName: 'Клиенты' },
    ...(user?.role === 'admin' || user?.role === 'superadmin'
      ? [{ name: 'Пользователи', href: '/users', icon: UsersIcon, shortName: 'Пользователи' }]
      : []),
    ...(user?.role === 'operator' || user?.role === 'admin' || user?.role === 'superadmin'
      ? [{ name: 'Смены', href: '/shifts', icon: ClockIcon, shortName: 'Смены' }]
      : []),
    ...(user?.role === 'admin' || user?.role === 'superadmin'
      ? [{ name: 'Мониторинг смен', href: '/shifts/monitoring', icon: PresentationChartLineIcon, shortName: 'Смены' }]
      : []),
    ...((user?.role === 'operator' && user?.supportLine === 2) || user?.role === 'admin' || user?.role === 'superadmin'
      ? [{ name: '2-я линия', href: '/second-line', icon: ArrowTrendingUpIcon, shortName: '2-я линия' }]
      : []),
    ...((user?.role === 'operator' && user?.supportLine === 2) || user?.role === 'admin' || user?.role === 'superadmin'
      ? [{ name: 'Рассылка', href: '/broadcasts', icon: MegaphoneIcon, shortName: 'Рассылка' }]
      : []),
    { name: 'База знаний', href: '/kb', icon: BookOpenIcon, shortName: 'БЗ' },
    { name: 'Лидерборд', href: '/leaderboard', icon: TrophyIcon, shortName: 'Лидерборд' },
    ...(user?.role === 'admin' || user?.role === 'superadmin'
      ? [{ name: 'Отчеты', href: '/reports', icon: DocumentChartBarIcon, shortName: 'Отчеты' }]
      : []),
    ...(user?.role === 'admin' || user?.role === 'superadmin'
      ? [{ name: 'Финансы', href: '/finance', icon: BanknotesIcon, shortName: 'Финансы' }]
      : []),
    ...(user?.role === 'superadmin'
      ? [{ name: 'SLA Настройки', href: '/sla-settings', icon: ShieldCheckIcon, shortName: 'SLA' }]
      : []),
    { name: 'Статистика', href: '/stats', icon: ChartPieIcon, shortName: 'Статистика' },
    { name: 'Настройки', href: '/settings', icon: Cog6ToothIcon, shortName: 'Настройки' },
  ];

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-foreground/50" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-80 flex-col shadow-xl bg-sidebar">
          <div className="flex h-16 shrink-0 items-center justify-between px-6 border-b border-sidebar-border">
            <h1 className="text-xl font-bold text-sidebar-foreground">GGDesk CRM</h1>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                className="p-2 rounded-md transition-colors text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent"
                onClick={toggleTheme}
              >
                {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
              </button>
              <button
                type="button"
                className="transition-colors text-sidebar-foreground/60 hover:text-sidebar-foreground"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          
          {/* User info in mobile sidebar */}
          <div className="px-6 py-4 border-b border-sidebar-border">
            <div className="flex items-center space-x-3">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.firstName}
                  className="h-12 w-12 rounded-full"
                />
              ) : (
                <UserCircleIcon className="h-12 w-12 text-sidebar-foreground/40" />
              )}
              <div>
                <p className="text-sm font-medium text-sidebar-foreground">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-sidebar-foreground/60">{user?.role}</p>
              </div>
            </div>
          </div>
          
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-4 h-6 w-6" />
                  {item.name}
                </Link>
              );
            })}
            
            {/* Logout button in mobile sidebar */}
            <div className="pt-4 mt-4 border-t border-sidebar-border">
              <button
                onClick={handleLogout}
                className="flex w-full items-center px-4 py-3 text-base font-medium rounded-lg transition-colors text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
              >
                <ArrowRightOnRectangleIcon className="h-6 w-6 mr-4" />
                Выйти
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col border-r border-sidebar-border bg-sidebar">
        <div className="flex h-16 shrink-0 items-center justify-between px-6 border-b border-sidebar-border">
          <h1 className="text-xl font-bold text-sidebar-foreground">GGDesk CRM</h1>
          <button
            type="button"
            className="p-2 rounded-md transition-colors text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent"
            onClick={toggleTheme}
          >
            {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
          </button>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-2">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                }`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col min-h-0 lg:pl-64">
        {/* Header */}
        <header className="shrink-0 shadow-sm border-b border-border bg-card">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              type="button"
              className="transition-colors lg:hidden text-foreground/60 hover:text-foreground"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            <div className="flex items-center space-x-4">
              {/* Sound on/off for the global new-ticket ding. The bell shows
                  the CURRENT state — bell = sound is on, slashed bell = muted. */}
              <button
                type="button"
                onClick={toggleSound}
                className="p-2 rounded-md transition-colors text-foreground/60 hover:text-foreground hover:bg-accent"
                title={soundMuted ? 'Звук уведомлений выключен — нажмите чтобы включить' : 'Звук уведомлений включён'}
                aria-label={soundMuted ? 'Включить звук' : 'Выключить звук'}
              >
                {soundMuted ? (
                  <BellSlashIcon className="h-5 w-5" />
                ) : (
                  <BellIcon className="h-5 w-5" />
                )}
              </button>

              {/* Push Notification Manager */}
              <PushNotificationManager />

              {/* Quick shift switcher — start/break/resume/end without
                  leaving the current page. Hidden for non-operator roles. */}
              <ShiftQuickControl />

              <div className="flex items-center space-x-3">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.firstName}
                    className="h-8 w-8 rounded-full"
                  />
                ) : (
                  <UserCircleIcon className="h-8 w-8 text-foreground/40" />
                )}
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-foreground">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground">{user?.role}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="hidden sm:flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors text-foreground/70 hover:text-foreground hover:bg-accent"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                Выйти
              </button>
            </div>
          </div>
        </header>

        {/* Push-уведомления: просит включить / тихо перерегистрирует токен. */}
        <NotificationPrompt />

        {/* Page content — scrolls inside its own column so the bottom bar
            below always sits at the actual bottom of the viewport, regardless
            of whether the window is full-height or split in half. */}
        <main className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 lg:p-6 xl:p-8">
          {children}
        </main>

        {/* Mobile bottom navigation — part of the flex column, NOT fixed,
            so short windows don't get content hidden behind it. */}
        <nav className="shrink-0 z-30 lg:hidden border-t border-border bg-card pb-[env(safe-area-inset-bottom)]">
          <div className="flex justify-around">
            {navigation.slice(0, 5).map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex flex-col items-center py-3 px-2 min-w-0 flex-1 transition-colors ${
                    isActive
                      ? 'text-primary'
                      : 'text-muted-foreground'
                  }`}
                >
                  <item.icon className={`h-6 w-6 mb-1`} />
                  <span className="text-xs font-medium truncate">{item.shortName}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Layout;